import { useEffect } from 'react';

import { getEventQueue } from '../../../utils/eventQueue';
import { type TAdBannerEventPrefix } from '../../../types/events';

interface IUseAdBannerMicrofrontendEventsParams {
  placeId: string;
  isReady: boolean;
  onAdStub?: () => void;
  onAdError?: () => void;
}

/**
 * Подписка на lifecycle микрофронтенда баннера по host-generated placeId. На `stub`/`error` —
 * колбэки `onAdStub`/`onAdError` (хост оставляет дефолтный фон под баннером).
 *
 * События приходят на глобальную `window.eventQueue` (синглтон `@cian/event-queue`
 * `Queue`), доступ к которой получаем через общий `getEventQueue`.
 */
export function useAdBannerMicrofrontendEvents({
  placeId,
  isReady,
  onAdStub,
  onAdError,
}: IUseAdBannerMicrofrontendEventsParams): void {
  useEffect(() => {
    // Если микрофронт не готов или не передан ни один колбэк, подписка не нужна.
    if (!isReady || (!onAdStub && !onAdError)) {
      return;
    }

    const eventQueue = getEventQueue();

    const prefix: TAdBannerEventPrefix = `ad-banner-frontend-reporting.v1.${placeId}.ad-banner-`;
    const stubEventName = `${prefix}stub` as const;
    const errorEventName = `${prefix}error` as const;

    if (onAdStub) {
      eventQueue.on(stubEventName, onAdStub);
    }

    if (onAdError) {
      eventQueue.on(errorEventName, onAdError);
    }

    return () => {
      if (onAdStub) {
        eventQueue.off(stubEventName, onAdStub);
      }

      if (onAdError) {
        eventQueue.off(errorEventName, onAdError);
      }
    };
  }, [placeId, isReady, onAdStub, onAdError]);
}
